/**
 * Holiday badges for the calendars (Google Calendar style).
 * Computed locally — no API, works offline. Canada + US sets, picked by the
 * business timezone (defaults to Canada, our primary market).
 */

export type Holiday = {
  /** Local date key, YYYY-MM-DD. */
  date: string;
  name: string;
};

export type HolidayCountry = 'CA' | 'US';

const CANADIAN_TZ = new Set([
  'America/St_Johns',
  'America/Halifax',
  'America/Glace_Bay',
  'America/Moncton',
  'America/Goose_Bay',
  'America/Toronto',
  'America/Nipigon',
  'America/Thunder_Bay',
  'America/Iqaluit',
  'America/Pangnirtung',
  'America/Winnipeg',
  'America/Rainy_River',
  'America/Resolute',
  'America/Rankin_Inlet',
  'America/Regina',
  'America/Swift_Current',
  'America/Edmonton',
  'America/Cambridge_Bay',
  'America/Yellowknife',
  'America/Inuvik',
  'America/Dawson_Creek',
  'America/Fort_Nelson',
  'America/Vancouver',
  'America/Whitehorse',
  'America/Dawson',
  'America/Creston',
  'America/Montreal',
]);

/** Best-effort country from an IANA timezone; defaults to Canada. */
export function holidayCountryFromTimezone(timezone?: string | null): HolidayCountry {
  const tz = (timezone || '').trim();
  if (!tz) return 'CA';
  if (CANADIAN_TZ.has(tz)) return 'CA';
  if (tz.startsWith('America/')) return 'US';
  return 'CA';
}

function key(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Nth (1-based) occurrence of a weekday (0=Sun..6=Sat) in a month (1-based). */
function nthWeekday(year: number, month: number, weekday: number, n: number): string {
  const first = new Date(year, month - 1, 1).getDay();
  const day = 1 + ((weekday - first + 7) % 7) + (n - 1) * 7;
  return key(year, month, day);
}

/** Last occurrence of a weekday in a month. */
function lastWeekday(year: number, month: number, weekday: number): string {
  const lastDay = new Date(year, month, 0); // last day of month
  const diff = (lastDay.getDay() - weekday + 7) % 7;
  return key(year, month, lastDay.getDate() - diff);
}

/** First weekday on/after a given day of month (e.g. Victoria Day: Monday on/before May 25 handled separately). */
function weekdayOnOrBefore(year: number, month: number, day: number, weekday: number): string {
  const d = new Date(year, month - 1, day);
  const diff = (d.getDay() - weekday + 7) % 7;
  return key(year, month, day - diff);
}

/** Gregorian Easter Sunday (Meeus/Jones/Butcher). */
function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function offsetKey(base: Date, days: number): string {
  const d = new Date(base);
  d.setDate(d.getDate() + days);
  return key(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/** Observances shown in both countries (like Google Calendar's holiday feeds). */
function sharedObservances(year: number): Holiday[] {
  const easter = easterSunday(year);
  return [
    { date: key(year, 1, 1), name: "New Year's Day" },
    { date: key(year, 2, 14), name: "Valentine's Day" },
    { date: key(year, 3, 17), name: "St. Patrick's Day" },
    { date: offsetKey(easter, -2), name: 'Good Friday' },
    { date: key(easter.getFullYear(), easter.getMonth() + 1, easter.getDate()), name: 'Easter Sunday' },
    { date: nthWeekday(year, 5, 0, 2), name: "Mother's Day" },
    { date: nthWeekday(year, 6, 0, 3), name: "Father's Day" },
    { date: key(year, 10, 31), name: 'Halloween' },
    { date: key(year, 12, 24), name: 'Christmas Eve' },
    { date: key(year, 12, 25), name: 'Christmas Day' },
    { date: key(year, 12, 31), name: "New Year's Eve" },
  ];
}

function canadaHolidays(year: number): Holiday[] {
  const easter = easterSunday(year);
  return [
    ...sharedObservances(year),
    { date: nthWeekday(year, 2, 1, 3), name: 'Family Day' },
    { date: offsetKey(easter, 1), name: 'Easter Monday' },
    { date: weekdayOnOrBefore(year, 5, 24, 1), name: 'Victoria Day' },
    { date: key(year, 6, 24), name: 'Saint-Jean-Baptiste Day' },
    { date: key(year, 7, 1), name: 'Canada Day' },
    { date: nthWeekday(year, 8, 1, 1), name: 'Civic Holiday' },
    { date: nthWeekday(year, 9, 1, 1), name: 'Labour Day' },
    { date: key(year, 9, 30), name: 'Truth and Reconciliation Day' },
    { date: nthWeekday(year, 10, 1, 2), name: 'Thanksgiving' },
    { date: key(year, 11, 11), name: 'Remembrance Day' },
    { date: key(year, 12, 26), name: 'Boxing Day' },
  ];
}

function usHolidays(year: number): Holiday[] {
  return [
    ...sharedObservances(year),
    { date: nthWeekday(year, 1, 1, 3), name: 'Martin Luther King Jr. Day' },
    { date: nthWeekday(year, 2, 1, 3), name: "Presidents' Day" },
    { date: lastWeekday(year, 5, 1), name: 'Memorial Day' },
    { date: key(year, 6, 19), name: 'Juneteenth' },
    { date: key(year, 7, 4), name: 'Independence Day' },
    { date: nthWeekday(year, 9, 1, 1), name: 'Labor Day' },
    { date: nthWeekday(year, 10, 1, 2), name: 'Columbus Day' },
    { date: key(year, 11, 11), name: 'Veterans Day' },
    { date: nthWeekday(year, 11, 4, 4), name: 'Thanksgiving' },
  ];
}

const cache = new Map<string, Map<string, string>>();

/** date-key (YYYY-MM-DD) → holiday name for one year+country, cached. */
export function holidayMapForYear(year: number, country: HolidayCountry): Map<string, string> {
  const cacheKey = `${country}-${year}`;
  let map = cache.get(cacheKey);
  if (!map) {
    map = new Map();
    const list = country === 'US' ? usHolidays(year) : canadaHolidays(year);
    for (const h of list) map.set(h.date, h.name);
    cache.set(cacheKey, map);
  }
  return map;
}

/** Holiday name for a YYYY-MM-DD date key, or null. */
export function holidayNameForDate(dateKey: string, country: HolidayCountry): string | null {
  const year = Number(dateKey.slice(0, 4));
  if (!Number.isFinite(year)) return null;
  return holidayMapForYear(year, country).get(dateKey) || null;
}
